import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, Loader2, Check } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { translations } from '../../translations';

interface ResetPasswordProps {
  isDarkMode: boolean;
  language: string;
}

export function ResetPassword({ isDarkMode, language }: ResetPasswordProps) {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [canReset, setCanReset] = useState(false);
  const t = translations[language as keyof typeof translations];

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setCanReset(!!session);
    };
    
    checkSession();
  }, []);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canReset) {
      setError(t.invalid_reset_link);
      return;
    }

    if (password !== confirmPassword) {
      setError(t.passwords_not_match);
      return;
    }

    if (password.length < 6) {
      setError(t.password_min_length);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      setSuccess(true);
      
      // Redirect to login after a brief success message
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err)
    {
      console.error('Error resetting password:', err);
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  const getWelcomeImage = () => {
    switch (language) {
      case 'FR':
        return 'https://ltyddaykojdpnqqlxhzz.supabase.co/storage/v1/object/public/avatars/welcome/FR.png';
      case 'EN':
        return 'https://ltyddaykojdpnqqlxhzz.supabase.co/storage/v1/object/public/avatars/welcome/EN.png';
      case 'ES':
        return 'https://ltyddaykojdpnqqlxhzz.supabase.co/storage/v1/object/public/avatars/welcome/ES.png';
      case 'RU':
        return 'https://ltyddaykojdpnqqlxhzz.supabase.co/storage/v1/object/public/avatars/welcome/RU.png';
      default:
        return 'https://ltyddaykojdpnqqlxhzz.supabase.co/storage/v1/object/public/avatars/welcome/EN.png';
    }
  };

  if (success) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${isDarkMode ? 'bg-[#121212]' : 'bg-gray-50'}`}>
        <div className={`w-full max-w-md p-6 rounded-lg ${
          isDarkMode ? 'bg-[#1c1c1c] border border-[#2b2b2b]' : 'bg-white shadow-lg'
        }`}>
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <Check className="text-emerald-500" size={24} />
            </div>
            <h2 className={`text-xl font-semibold mb-2 ${
              isDarkMode ? 'text-gray-200' : 'text-gray-900'
            }`}>
              {t.password_updated}
            </h2>
            <p className={`text-sm ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {t.redirecting_to_login}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col md:flex-row ${isDarkMode ? 'bg-[#121212]' : 'bg-gray-50'}`}>
      {/* Left side - Form */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-4">
        <div className={`w-full max-w-md p-6 rounded-lg ${
          isDarkMode ? 'bg-[#1c1c1c] border border-[#2b2b2b]' : 'bg-white shadow-lg'
        }`}>
          <h2 className={`text-xl font-semibold mb-6 ${
            isDarkMode ? 'text-gray-200' : 'text-gray-900'
          }`}>
            {t.reset_your_password}
          </h2>

          {error && (
            <div className="mb-4 p-3 rounded bg-red-100 border border-red-200 text-red-600 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {t.new_password}
              </label>
              <div className="relative">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm ${
                    isDarkMode
                      ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                      : 'bg-gray-50 text-gray-900 border-gray-200'
                  } focus:outline-none focus:ring-2 focus:ring-orange-500`}
                  placeholder={t.new_password_placeholder}
                  required
                />
                <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1.5 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {t.confirm_password}
              </label>
              <div className="relative">
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm ${
                    isDarkMode
                      ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                      : 'bg-gray-50 text-gray-900 border-gray-200'
                  } focus:outline-none focus:ring-2 focus:ring-orange-500`}
                  placeholder={t.confirm_password_placeholder}
                  required
                />
                <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading || !canReset}
              className="w-full bg-orange-500 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-orange-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  {t.updating}
                </>
              ) : (
                t.reset_password
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Right side - Welcome image */}
      <div 
        className="hidden md:block md:w-1/2 bg-cover bg-center"
        style={{
          backgroundImage: `url("${getWelcomeImage()}")`
        }}
      />
    </div>
  );
}